import { useEffect, useRef, useState } from 'react';
import emendaService, { type EmendaAnexoDTO, type EmendaHistoricoDTO, type EvidenciaTaepUpload } from '../services/emendaService';

function formatUsuario(usuario: any): string {
  if (!usuario) return 'Sistema';
  if (typeof usuario === 'string') return usuario;
  const candidates = ['name', 'nome', 'fullName', 'username', 'login', 'email', 'user', 'displayName'];
  for (const key of candidates) {
    const val = usuario[key];
    if (typeof val === 'string' && val.trim()) return val;
  }
  try {
    if (usuario && typeof usuario === 'object') {
      if (usuario.user && typeof usuario.user === 'object') {
        for (const k of candidates) {
          const v = usuario.user[k];
          if (typeof v === 'string' && v.trim()) return v;
        }
      }
      if (usuario.id) return String(usuario.id);
    }
  } catch (_) {}
  return String(usuario || '');
}

export default function useEmendaAttachments(emendaId: string | undefined, currentUser?: any) {
  const [anexos, setAnexos] = useState<EmendaAnexoDTO[]>([]);
  const [historico, setHistorico] = useState<EmendaHistoricoDTO[]>([]);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const refreshAnexos = async () => {
    if (!emendaId) return;
    try {
      const a = await emendaService.listAnexos(emendaId);
      setAnexos(a || []);
    } catch (err) {
      setAnexos([]);
    }
  };

  const refreshHistorico = async () => {
    if (!emendaId) return;
    try {
      const h = await emendaService.getHistorico(emendaId);
      const normalized = (h || []).map((x) => ({ ...x, usuario: formatUsuario(x.usuario) }));
      setHistorico(normalized);
    } catch (err) {
      setHistorico([]);
    }
  };

  useEffect(() => {
    refreshAnexos();
    refreshHistorico();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emendaId]);

  const uploadFile = async (file: File, evidencia?: EvidenciaTaepUpload) => {
    if (!emendaId) return;
    setUploading(true);
    try {
      const novo = await emendaService.uploadAnexo(emendaId, file, undefined, false, evidencia);
      setAnexos((prev) => [...prev, novo]);

      // Try to refresh historico and detect server entry
      let usedServerHist = false;
      try {
        const h = await emendaService.getHistorico(emendaId);
        const normalized = (h || []).map((x) => ({ ...x, usuario: formatUsuario(x.usuario) }));
        const matchesUpload = normalized.some((hitem) => {
          const obs = (hitem.observacao || '').toString();
          if (obs.includes((novo as any).nomeOriginal || '')) return true;
          if ((hitem.acao || '').toUpperCase().includes('ANEX')) return true;
          if ((novo as any).dataUpload && hitem.dataHora) {
            const t1 = new Date((novo as any).dataUpload).getTime();
            const t2 = new Date(hitem.dataHora).getTime();
            if (!Number.isNaN(t1) && Math.abs(t1 - t2) < 5000) return true;
          }
          return false;
        });
        setHistorico(normalized);
        if (matchesUpload) usedServerHist = true;
      } catch (err) {
        // ignore
      }

      if (!usedServerHist) {
        const usernameFromNovo = (novo as any).usuarioUpload;
        const username = usernameFromNovo ? formatUsuario(usernameFromNovo) : formatUsuario(currentUser);
        const now = (novo as any).dataUpload || new Date().toISOString();
        const newHist: EmendaHistoricoDTO = {
          id: Date.now(),
          emendaId: emendaId,
          acao: 'ANEXO_ADICIONADO',
          observacao: `Anexo: ${novo.nomeOriginal}`,
          usuario: username,
          dataHora: now,
        };
        setHistorico((prev) => [...(prev || []), newHist]);
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  // Create a new File object with ' - Despacho' appended before the extension (kept for UX but server-side flag is authoritative)
  const uploadFileDespacho = async (file: File) => {
    const renameForDespacho = (name: string) => {
      if (!name) return name;
      const lastDot = name.lastIndexOf('.');
      if (lastDot <= 0) return `${name} - Despacho`;
      const base = name.substring(0, lastDot);
      const ext = name.substring(lastDot);
      return `${base} - Despacho${ext}`;
    };
    const newName = renameForDespacho(file.name || 'anexo');
    let newFile: File;
    try {
      newFile = new File([file], newName, { type: file.type });
    } catch (e) {
      const blob = new Blob([file], { type: file.type });
      // @ts-ignore
      newFile = Object.assign(blob, { name: newName, lastModified: Date.now() });
    }
    // Use service upload with isDespacho flag so backend stores the flag reliably
    if (!emendaId) return;
    setUploading(true);
    try {
      const novo = await emendaService.uploadAnexo(emendaId, newFile as File, undefined, true);
      setAnexos((prev) => [...prev, novo]);

      // refresh historico and rest of logic (same as uploadFile)
      let usedServerHist = false;
      try {
        const h = await emendaService.getHistorico(emendaId);
        const normalized = (h || []).map((x) => ({ ...x, usuario: formatUsuario(x.usuario) }));
        const matchesUpload = normalized.some((hitem) => {
          const obs = (hitem.observacao || '').toString();
          if (obs.includes((novo as any).nomeOriginal || '')) return true;
          if ((hitem.acao || '').toUpperCase().includes('ANEX')) return true;
          if ((novo as any).dataUpload && hitem.dataHora) {
            const t1 = new Date((novo as any).dataUpload).getTime();
            const t2 = new Date(hitem.dataHora).getTime();
            if (!Number.isNaN(t1) && Math.abs(t1 - t2) < 5000) return true;
          }
          return false;
        });
        setHistorico(normalized);
        if (matchesUpload) usedServerHist = true;
      } catch (err) {
        // ignore
      }

      if (!usedServerHist) {
        const usernameFromNovo = (novo as any).usuarioUpload;
        const username = usernameFromNovo ? formatUsuario(usernameFromNovo) : formatUsuario(currentUser);
        const now = (novo as any).dataUpload || new Date().toISOString();
        const newHist: EmendaHistoricoDTO = {
          id: Date.now(),
          emendaId: emendaId,
          acao: 'ANEXO_ADICIONADO',
          observacao: `Anexo: ${novo.nomeOriginal}`,
          usuario: username,
          dataHora: now,
        };
        setHistorico((prev) => [...(prev || []), newHist]);
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const deleteAnexo = async (anexoId: string) => {
    if (!emendaId) return;
    setDeletingId(anexoId);
    try {
      await emendaService.deleteAnexo(emendaId, anexoId);
      setAnexos((prev) => prev.filter((a) => a.id !== anexoId));
      // refresh historico to reflect deletion if backend logs it
      await refreshHistorico();
    } catch (err) {
      // ignore
    } finally {
      setDeletingId(null);
    }
  };

  return {
    anexos,
    historico,
    uploading,
    deletingId,
    inputRef,
    refreshAnexos,
    refreshHistorico,
    uploadFile,
    uploadFileDespacho,
    deleteAnexo,
  };
}
